import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FileText, LayoutDashboard, Users, LogOut, ChevronsRight, Shield, Briefcase, Sparkles, Menu, Gauge, Building2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

const Navbar = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <FileText className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight flex items-center">
            ResumePro<span className="text-primary">24</span>
            <ChevronsRight className="h-5 w-5 text-accent ml-0.5" strokeWidth={3} />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link to="/experts">
            <Button variant="ghost" className="font-body" data-testid="nav-experts">
              <Users className="h-4 w-4 mr-1.5" /> Experts
            </Button>
          </Link>
          <Link to="/jobs">
            <Button variant="ghost" className="font-body" data-testid="nav-jobs">
              <Briefcase className="h-4 w-4 mr-1.5" /> Jobs
            </Button>
          </Link>
          <Link to="/recruiters">
            <Button variant="ghost" className="font-body" data-testid="nav-recruiters">
              <Building2 className="h-4 w-4 mr-1.5" /> For Recruiters
            </Button>
          </Link>
          <Link to="/improve">
            <Button variant="ghost" className="font-body" data-testid="nav-improve">
              <Sparkles className="h-4 w-4 mr-1.5" /> Improve Resume
            </Button>
          </Link>
          <Link to="/ats-score">
            <Button variant="ghost" className="font-body" data-testid="nav-ats">
              <Gauge className="h-4 w-4 mr-1.5" /> ATS Score
            </Button>
          </Link>
          {user && (
            <Link to="/dashboard">
              <Button variant="ghost" className="font-body" data-testid="nav-dashboard">
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
              </Button>
            </Link>
          )}
          {user?.is_expert && (
            <Link to="/expert">
              <Button variant="ghost" className="font-body" data-testid="nav-expert-portal">
                <Briefcase className="h-4 w-4 mr-1.5" /> Expert Portal
              </Button>
            </Link>
          )}
          {user?.is_admin && (
            <Link to="/admin">
              <Button variant="ghost" className="font-body text-primary" data-testid="nav-admin">
                <Shield className="h-4 w-4 mr-1.5" /> Admin
              </Button>
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu">
                <Avatar className="h-9 w-9 border border-border cursor-pointer">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold font-body truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                {user.is_expert && (
                  <DropdownMenuItem onClick={() => navigate("/expert")} data-testid="menu-expert">
                    <Briefcase className="h-4 w-4 mr-2" /> Expert Portal
                  </DropdownMenuItem>
                )}
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="h-4 w-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={login} className="rounded-full font-body font-semibold" data-testid="nav-login-btn">
              Sign in
            </Button>
          )}

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="nav-mobile-toggle">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-heading">
                  ResumePro<span className="text-primary">24</span>
                  <ChevronsRight className="h-4 w-4 text-accent" strokeWidth={3} />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                <SheetClose asChild>
                  <Link to="/experts" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-experts">
                    <Users className="h-4 w-4 text-primary" /> Experts
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/jobs" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-jobs">
                    <Briefcase className="h-4 w-4 text-primary" /> Jobs
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/recruiters" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-recruiters">
                    <Building2 className="h-4 w-4 text-primary" /> For Recruiters
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/improve" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-improve">
                    <Sparkles className="h-4 w-4 text-primary" /> Improve Resume
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/ats-score" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-ats">
                    <Gauge className="h-4 w-4 text-primary" /> ATS Score
                  </Link>
                </SheetClose>
                {user && (
                  <SheetClose asChild>
                    <Link to="/dashboard" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-dashboard">
                      <LayoutDashboard className="h-4 w-4 text-primary" /> Dashboard
                    </Link>
                  </SheetClose>
                )}
                {user?.is_expert && (
                  <SheetClose asChild>
                    <Link to="/expert" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium hover:bg-secondary transition-colors" data-testid="mobile-nav-expert">
                      <Briefcase className="h-4 w-4 text-primary" /> Expert Portal
                    </Link>
                  </SheetClose>
                )}
                {user?.is_admin && (
                  <SheetClose asChild>
                    <Link to="/admin" className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-sm font-medium text-primary hover:bg-secondary transition-colors" data-testid="mobile-nav-admin">
                      <Shield className="h-4 w-4" /> Admin
                    </Link>
                  </SheetClose>
                )}
                {!user && (
                  <SheetClose asChild>
                    <Button onClick={login} className="mt-4 rounded-full font-semibold" data-testid="mobile-nav-login">
                      Sign in
                    </Button>
                  </SheetClose>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
